import type { MailboxInfo, MailboxKey } from 'owa-client-types';
import type { MailboxGroup } from 'owa-mail-list-store';

export interface FanOutResult<T> {
    result?: T;
    error?: Error;
}

/**
 * Executes a triage action per mailbox group in parallel, collecting per-group results.
 * This is a low-level execution primitive — it only handles the parallel server calls.
 * Callers are responsible for local lies (before) and rollback/notification (after).
 *
 * The execute callback may return a Promise (for async service calls) or return
 * synchronously / return void (for fire-and-forget action dispatches). Both patterns
 * are handled uniformly via await.
 */
export async function fanOutTriageActionByMailbox<T = void>(
    groups: Map<MailboxKey, MailboxGroup>,
    execute: (mailboxInfo: MailboxInfo, rowKeys: string[]) => Promise<T> | T | void
): Promise<Map<MailboxKey, FanOutResult<T>>> {
    const entries = [...groups.entries()];

    const settled = await Promise.allSettled(
        entries.map(async ([mailboxKey, group]): Promise<[MailboxKey, FanOutResult<T>]> => {
            try {
                const result = (await execute(group.mailboxInfo, group.rowKeys)) as T | undefined;
                return [mailboxKey, { result }];
            } catch (error) {
                return [mailboxKey, { error: error as Error }];
            }
        })
    );

    const results = new Map<MailboxKey, FanOutResult<T>>();
    for (const entry of settled) {
        // All promises resolve because of the internal try/catch, so we only check for 'fulfilled' status.
        // If it's not fulfilled, we skip it and the caller will see a missing key in the results.
        if (entry.status === 'fulfilled') {
            const [key, fanOutResult] = entry.value;
            results.set(key, fanOutResult);
        }
    }

    return results;
}

/**
 * Returns true if all groups in the fan-out result succeeded.
 */
export function didAllGroupsSucceed<T>(results: Map<MailboxKey, FanOutResult<T>>): boolean {
    for (const result of results.values()) {
        if (result.error) {
            return false;
        }
    }
    return true;
}
